import { defineStore } from 'pinia';
import api from '@/services/api';

/**
 * Categories store - gerencia todos os dados e operações relacionados a categorias
 */
export const useCategoriesStore = defineStore('categories', {
  // State
  state: () => ({
    categories: [],
    currentCategory: null,
    isLoading: false,
    error: null,
    // Paginação e Filtros
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
    // Paginação para fornecedores na visualização de categoria
    suppliersPagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
    // Paginação para fornecedores das subcategorias
    subSuppliersPagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
    filters: {
      name: '',
      parentId: null,
      onlyParents: true, // Quando true com onlyNames=true, subcategorias não são incluídas
      active: true,
    },
    // Ordenação
    sorting: {
      sortBy: 'name', // Padrão agora é 'name' ao invés de 'createdAt'
      createdAtOrder: 'asc', // Padrão agora é 'asc' para ordem alfabética crescente
    },
    // Ordenação para fornecedores
    suppliersSorting: {
      sortOrder: 'desc',
    },
    // Ordenação para fornecedores das subcategorias
    subSuppliersSorting: {
      sortOrder: 'desc',
    },
    // Estado para importação de CSV
    importState: {
      isImporting: false,
      importError: null,
      importResults: null,
    },
  }),

  // Getters
  getters: {
    // Get a specific category by ID
    getCategoryById: (state) => (id) => {
      return state.categories.find((category) => category.id === id);
    },

    // Get all parent categories (categories without a parent)
    parentCategories: (state) => {
      return state.categories.filter((category) => !category.parentId);
    },

    // Get child categories for a specific parent
    getChildCategories: (state) => (parentId) => {
      return state.categories.filter(
        (category) => category.parentId === parentId
      );
    },

    // Get categories as a hierarchical tree
    categoryTree: (state) => {
      const buildTree = (parentId = null) => {
        return state.categories
          .filter((category) => category.parentId === parentId)
          .map((category) => ({
            ...category,
            children: buildTree(category.id),
          }));
      };

      return buildTree();
    },

    // Get categories as flat options array for dropdowns/selects
    categoryOptions() {
      const flattenTree = (tree, depth = 0) => {
        let result = [];

        tree.forEach((node) => {
          result.push({
            id: node.id,
            name: '  '.repeat(depth) + node.name,
            value: node.id,
          });

          if (node.children && node.children.length > 0) {
            result = [...result, ...flattenTree(node.children, depth + 1)];
          }
        });

        return result;
      };

      return flattenTree(this.categoryTree);
    },
  },

  // Actions
  actions: {
    /**
     * Fetch categories with filters, pagination and sorting
     * @param {Object} options - Options for fetching categories
     * @param {Object} options.filters - Filter parameters { name, parentId, onlyParents, active }
     * @param {Object} options.pagination - Pagination parameters { page, limit }
     * @param {Object} options.sorting - Sorting parameters { sortBy, createdAtOrder }
     * @param {boolean} options.forceRefresh - Force refresh even if loading
     * @returns {Promise<Array>} - The fetched categories
     * 
     * Nota: Quando onlyParents=true e onlyNames=true são usados juntos,
     * as subcategorias não serão incluídas na resposta da API.
     */
    async fetchCategories(options = {}) {
      // Se já estiver carregando e não for um forceRefresh, retorne
      if (this.isLoading && !options.forceRefresh) return;

      this.isLoading = true;
      this.error = null;

      try {
        // Atualizar o estado com as opções fornecidas
        if (options.filters) {
          this.filters = { ...this.filters, ...options.filters };
        }

        if (options.pagination) {
          this.pagination = { ...this.pagination, ...options.pagination };
        }

        if (options.sorting) {
          this.sorting = { ...this.sorting, ...options.sorting };
        }

        // Compilar parâmetros para a requisição API
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          sortBy: this.sorting.sortBy,
          createdAtOrder: this.sorting.createdAtOrder,
        };

        // Adicionar filtros se não forem nulos/vazios
        if (this.filters.name) params.name = this.filters.name;
        if (this.filters.parentId) params.parentId = this.filters.parentId;
        if (this.filters.onlyParents)
          params.onlyParents = this.filters.onlyParents;
        if (this.filters.active !== undefined)
          params.active = this.filters.active;

        // Fazer a chamada API real
        const response = await api.categories.getAll(params);

        // Verificar se a resposta foi bem-sucedida
        if (response.data && response.data.success) {
          // Atualizar as categorias com os dados da API
          this.categories = response.data.data.data;

          // Atualizar informações de paginação
          if (response.data.data.meta) {
            const meta = response.data.data.meta;
            this.pagination = {
              ...this.pagination,
              total: meta.total,
              totalPages: meta.totalPages,
              page: meta.page,
              limit: meta.limit,
            };
          }
        } else {
          // Se a API retornou sucesso: false
          this.categories = [];
          this.pagination.total = 0;
          this.pagination.totalPages = 0;
          throw new Error('Falha ao obter categorias');
        }

        return this.categories;
      } catch (err) {
        this.error = err.message || 'Falha ao carregar categorias';
        console.error('Erro ao carregar categorias:', err);
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Fetch a single category by ID with its related suppliers
     * @param {string} id - The category ID
     * @param {Object} options - Pagination and sorting options
     * @returns {Promise<Object>} - The category object
     */
    async fetchCategoryById(id, options = {}) {
      this.isLoading = true;
      this.error = null;

      try {
        // Atualizar opções de paginação e ordenação se fornecidas
        if (options.suppliersPagination) {
          this.suppliersPagination = {
            ...this.suppliersPagination,
            ...options.suppliersPagination,
          };
        }

        if (options.subSuppliersPagination) {
          this.subSuppliersPagination = {
            ...this.subSuppliersPagination,
            ...options.subSuppliersPagination,
          };
        }

        if (options.suppliersSorting) {
          this.suppliersSorting = {
            ...this.suppliersSorting,
            ...options.suppliersSorting,
          };
        }

        if (options.subSuppliersSorting) {
          this.subSuppliersSorting = {
            ...this.subSuppliersSorting,
            ...options.subSuppliersSorting,
          };
        }

        // Montar parâmetros para a API
        const params = {
          suppliersPage: this.suppliersPagination.page,
          suppliersLimit: this.suppliersPagination.limit,
          subSuppliersPage: this.subSuppliersPagination.page,
          subSuppliersLimit: this.subSuppliersPagination.limit,
          suppliersSortOrder: this.suppliersSorting.sortOrder,
          subSuppliersSortOrder: this.subSuppliersSorting.sortOrder,
        };

        const response = await api.categories.getById(id, params);

        if (response.data && response.data.success) {
          const category = response.data.data;

          // Atualizar informações de paginação para fornecedores
          if (category.suppliers?.meta) {
            this.suppliersPagination = {
              ...this.suppliersPagination,
              total: category.suppliers.meta.total,
              totalPages: category.suppliers.meta.totalPages,
              page: category.suppliers.meta.page,
              limit: category.suppliers.meta.limit,
            };
          }

          // Atualizar informações de paginação para fornecedores das subcategorias
          if (category.subcategoriesSuppliers?.meta) {
            this.subSuppliersPagination = {
              ...this.subSuppliersPagination,
              total: category.subcategoriesSuppliers.meta.total,
              totalPages: category.subcategoriesSuppliers.meta.totalPages,
              page: category.subcategoriesSuppliers.meta.page,
              limit: category.subcategoriesSuppliers.meta.limit,
            };
          }

          // Atualiza o currentCategory
          this.currentCategory = category;
          return category;
        } else {
          throw new Error('Categoria não encontrada');
        }
      } catch (err) {
        this.error = err.message || 'Falha ao obter categoria';
        console.error('Erro ao obter categoria:', err);
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Add a new category
     * @param {Object} category - The category data to add
     * @returns {Promise<Object>} - The created category
     */
    async addCategory(categoryData) {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await api.categories.create(categoryData);

        if (response.data && response.data.success) {
          const newCategory = response.data.data;
          // Não atualizamos this.categories aqui - melhor refazer a busca
          // para garantir que temos dados consistentes
          return newCategory;
        } else {
          throw new Error('Falha ao criar categoria');
        }
      } catch (err) {
        this.error = err.message || 'Falha ao adicionar categoria';
        console.error('Erro ao adicionar categoria:', err);
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Update an existing category
     * @param {string} id - The category ID to update
     * @param {Object} updates - The category data to update
     * @returns {Promise<Object>} - The updated category
     */
    async updateCategory(id, updates) {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await api.categories.update(id, updates);

        if (response.data && response.data.success) {
          const updatedCategory = response.data.data;

          // Se estamos visualizando esta categoria, atualizar o currentCategory
          if (this.currentCategory && this.currentCategory.id === id) {
            this.currentCategory = updatedCategory;
          }

          // Atualizar na lista local se existir
          const index = this.categories.findIndex((c) => c.id === id);
          if (index !== -1) {
            this.categories[index] = updatedCategory;
          }

          return updatedCategory;
        } else {
          throw new Error('Falha ao atualizar categoria');
        }
      } catch (err) {
        this.error = err.message || 'Falha ao atualizar categoria';
        console.error('Erro ao atualizar categoria:', err);
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Delete a category
     * @param {string} id - The category ID to delete
     * @returns {Promise<boolean>} - Success status
     */
    async deleteCategory(id) {
      this.isLoading = true;
      this.error = null;

      try {
        const response = await api.categories.delete(id);

        if (response.data && response.data.success) {
          // Remover da lista local se existir
          const index = this.categories.findIndex((c) => c.id === id);
          if (index !== -1) {
            this.categories.splice(index, 1);
          }

          // Se estávamos visualizando esta categoria, limpar o currentCategory
          if (this.currentCategory && this.currentCategory.id === id) {
            this.currentCategory = null;
          }

          return true;
        } else {
          throw new Error('Falha ao excluir categoria');
        }
      } catch (err) {
        // Verificar se o erro é por dependências
        if (
          err.response?.data?.message?.includes('constraint') ||
          err.response?.data?.message?.includes('dependenc')
        ) {
          this.error =
            'Esta categoria não pode ser excluída porque existem fornecedores ou produtos vinculados a ela.';
        } else {
          this.error = err.message || 'Falha ao excluir categoria';
        }

        console.error('Erro ao excluir categoria:', err);
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Set current category
     * @param {Object} category - The category to set as current
     */
    setCurrentCategory(category) {
      this.currentCategory = category;
    },

    /**
     * Clear current category
     */
    clearCurrentCategory() {
      this.currentCategory = null;
    },

    /**
     * Set sorting parameters
     * @param {Object} sorting - { sortBy, createdAtOrder }
     */
    setSorting(sorting) {
      this.sorting = { ...this.sorting, ...sorting };
    },

    /**
     * Set filters
     * @param {Object} filters - { name, parentId, onlyParents, active }
     */
    setFilters(filters) {
      this.filters = { ...this.filters, ...filters };
    },

    /**
     * Set pagination
     * @param {Object} pagination - { page, limit }
     */
    setPagination(pagination) {
      this.pagination = { ...this.pagination, ...pagination };
    },

    /**
     * Reset filters to default values
     */
    resetFilters() {
      this.filters = {
        name: '',
        parentId: null,
        onlyParents: true, // Padrão é true para mostrar apenas categorias pai
        active: true,
      };
    },

    /**
     * Set pagination for suppliers
     * @param {Object} pagination - { page, limit }
     */
    setSuppliersPagination(pagination) {
      this.suppliersPagination = { ...this.suppliersPagination, ...pagination };
    },

    /**
     * Set pagination for subcategories suppliers
     * @param {Object} pagination - { page, limit }
     */
    setSubSuppliersPagination(pagination) {
      this.subSuppliersPagination = {
        ...this.subSuppliersPagination,
        ...pagination,
      };
    },

    /**
     * Set sorting for suppliers
     * @param {Object} sorting - { sortOrder }
     */
    setSuppliersSorting(sorting) {
      this.suppliersSorting = { ...this.suppliersSorting, ...sorting };
    },

    /**
     * Set sorting for subcategories suppliers
     * @param {Object} sorting - { sortOrder }
     */
    setSubSuppliersSorting(sorting) {
      this.subSuppliersSorting = { ...this.subSuppliersSorting, ...sorting };
    },

    /**
     * Import categories from a CSV file
     * @param {File} file - The CSV file to import
     * @returns {Promise<Object>} - The import results
     */
    async importCategories(file) {
      this.importState.isImporting = true;
      this.importState.importError = null;
      this.importState.importResults = null;

      try {
        // Create form data for file upload
        const formData = new FormData();
        formData.append('file', file);

        // Make API call to import endpoint
        const response = await api.categories.importCsv(formData);

        // Process response
        if (response.data) {
          // Padronizar o formato dos resultados
          // Primeiro, verificar se os dados estão aninhados em 'data'
          const responseData = response.data.data || response.data;

          // Construir um objeto de resultados padronizado
          const results = {
            success:
              responseData.success === true ||
              responseData.status === 'success',
            message: responseData.message || 'Importação realizada',
            total: responseData.total || responseData.totalProcessed || 0,
            totalProcessed:
              responseData.totalProcessed || responseData.total || 0,
            successCount: responseData.successCount || 0,
            errorCount: responseData.errorCount || 0,
            results: responseData.results || [],
          };

          // Se não tiver contagens específicas, mas tiver resultados, calcular
          if (
            results.results.length > 0 &&
            !results.successCount &&
            !results.errorCount
          ) {
            results.successCount = results.results.filter(
              (r) => r.success
            ).length;
            results.errorCount = results.results.filter(
              (r) => !r.success
            ).length;
            results.totalProcessed = results.results.length;
          }

          this.importState.importResults = results;

          // Refresh categories list after successful import
          if (results.success) {
            await this.fetchCategories({ forceRefresh: true });
          }

          return this.importState.importResults;
        } else {
          throw new Error('Falha ao importar categorias: resposta inválida');
        }
      } catch (err) {
        console.error('Erro ao importar categorias:', err);
        this.importState.importError =
          err.message || 'Falha ao importar categorias';
        throw err;
      } finally {
        this.importState.isImporting = false;
      }
    },

    /**
     * Reset import state
     */
    resetImportState() {
      this.importState = {
        isImporting: false,
        importError: null,
        importResults: null,
      };
    },
  },
});
